import { UiOption } from '@/lib/helpers';
import { PolicyAccessList } from '.';
import { PolicyModelV1 } from './policy-v1.model';
import { McpToolModel } from '../mcp-tool/mcp-tool.model';
import { orderBy } from 'lodash';

export type McpAction = 'read' | 'read-write';

export type McpBlockAction = 'ignore' | 'log' | 'delete';

export type McpControls = {
  action: McpAction;
  blockAction: McpBlockAction;
  services: PolicyAccessList;
  tools: PolicyAccessList;
};

export const McpActionOptions: UiOption[] = [
  {
    label: 'Read-only',
    value: 'read',
    description:
      'Tools that are able to read resources will be allowed. Tools that can create, update and make other changes will have the Catch-all Action applied.',
  },
  {
    label: 'All Capabilities',
    value: 'read-write',
    description:
      'All tools will have the Catch-all Action applied, regardless of their capabilities.',
  },
];

export const McpBlockActionOptions: UiOption[] = [
  { label: 'Allow', value: 'allow', meta: { header: true } },
  { label: 'Allow', value: 'ignore' },
  { label: 'Monitor', value: 'log' },
  { label: 'Disallow', value: 'disallow', meta: { header: true } },
  { label: 'Block', value: 'delete' },
];

type McpService = {
  name: string;
  id: string;
  providerId: string;
};

export const McpServices: McpService[] = [
  { name: 'CloudFlare Documentation MCP Server', id: 'cf-doc-server', providerId: 'cloudflare' },
  {
    name: 'CloudFlare Workers Bindings MCP Server',
    id: 'cf-workers-bindings-server',
    providerId: 'cloudflare',
  },
  { name: 'CloudFlare Radar MCP Server', id: 'cf-radar-server', providerId: 'cloudflare' },
  { name: 'AWS CloudFormation MCP Server', id: 'aws-cfn-server', providerId: 'aws' },
  { name: 'AWS Documentation MCP Server', id: 'aws-doc-server', providerId: 'aws' },
  { name: 'AWS CDK MCP', id: 'aws-cdk-server', providerId: 'aws' },
  { name: 'AWS Terraform MCP Server', id: 'aws-terraform-server', providerId: 'aws' },
  { name: 'GitHub MCP Server', id: 'github-server', providerId: 'github' },
  { name: 'Azure MCP Server', id: 'azure-server', providerId: 'azure' },
];

export const getMcpActionOptions = (orgPolicy?: PolicyModelV1): UiOption[] => {
  const actionOptions: UiOption[] = [];
  const orgAction = orgPolicy?.mcpControls?.action || '';
  const hasAllowAll = orgAction === 'read-write';

  McpActionOptions.forEach(({ value, label, description }) => {
    const niceOpt: UiOption = {
      value,
      label,
      description,
    };

    if (value === 'read' && hasAllowAll) {
      niceOpt.meta = { blockedByOrgPolicy: true };
      niceOpt.disabled = true;
    } else if (value === orgAction) {
      niceOpt.meta = { orgPolicyLevel: true };
    }

    actionOptions.push(niceOpt);
  });

  return actionOptions;
};

export const getMcpBlockActionOptions = (orgPolicy?: PolicyModelV1): UiOption[] => {
  const blockActionOptions: UiOption[] = [];
  const orgBlockAction = orgPolicy?.mcpControls?.blockAction;
  let allowed: McpBlockAction[] = ['ignore', 'log', 'delete'];

  if (orgBlockAction === 'log') {
    allowed = ['ignore', 'log'];
  } else if (orgBlockAction === 'ignore') {
    allowed = ['ignore'];
  }

  McpBlockActionOptions.forEach(({ value, label, meta }) => {
    const niceOpt: UiOption = {
      value,
      label,
      meta,
    };

    if (meta?.header) {
      blockActionOptions.push(niceOpt);
      return;
    }

    const allowedOpt = allowed.includes(value as McpBlockAction);
    const orgOpt = value === orgBlockAction;

    if (orgOpt) {
      niceOpt.meta = { orgPolicyLevel: true };
    } else if (!allowedOpt) {
      niceOpt.disabled = true;
      niceOpt.meta = { blockedByOrgPolicy: true };
    }

    blockActionOptions.push(niceOpt);
  });

  return blockActionOptions;
};

export const getMcpToolOptions = (
  mcpTools: McpToolModel[],
  aclVariant: 'allow' | 'block',
  policy: PolicyModelV1,
  orgPolicy?: PolicyModelV1
): UiOption[] => {
  const { enabled, disabled } = policy.mcpControls.tools;
  const alternateList = aclVariant === 'allow' ? disabled : enabled;

  const opts = mcpTools.map((mcpTool) => {
    const { id, name } = mcpTool;

    const disabledByAlternateList = alternateList.includes(id);
    const disabledByOrgPolicy = orgPolicy?.mcpControls.tools.disabled.includes(id);
    const enabledByOrgPolicy = orgPolicy?.mcpControls.tools.enabled.includes(id);

    return {
      label: name,
      value: id,
      meta: { disabledByAlternateList, enabledByOrgPolicy, disabledByOrgPolicy, mcpTool },
    };
  });

  return orderBy(opts, [({ meta }) => meta.mcpTool.serverName.toLowerCase()]);
};
